/**
 * Pure helpers for `useScheduleWithAiForm` (no React). Keeps the hook file focused on state and effects.
 */
import moment from 'moment-timezone';
import { formatTimezoneAbbreviationForDisplay } from '@/utils/formatTimezoneAbbreviation';
import { SCHEDULE_AI_COPY } from '../../constants';
import { COORDINATOR_LOCATION_OPTIONS, TIME_OPTIONS_15MIN } from '../options';

const COPY = SCHEDULE_AI_COPY;

/** Coerce plan / API office identifiers to a finite number for form state and selects. */
export function normalizePlanOfficeId(plan: unknown): number | null {
  if (!plan || typeof plan !== 'object') return null;
  const p = plan as {
    ats_office?: { id?: unknown } | null;
    ats_office_id?: unknown;
  };
  const nested = p.ats_office?.id;
  const raw =
    nested !== undefined && nested !== null ? nested : p.ats_office_id;
  if (raw === undefined || raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** First location value that is enabled in availableLocations (for defaulting when plan has no location). */
export function getFirstAvailableLocation(availableLocations: unknown): string {
  const locationsMap =
    (availableLocations as { locations?: Record<string, boolean> })
      ?.locations ?? (availableLocations as Record<string, boolean>);
  if (!locationsMap || typeof locationsMap !== 'object') return '';
  const first = COORDINATOR_LOCATION_OPTIONS.find(
    (opt) => (locationsMap as Record<string, boolean>)[opt.value] === true
  );
  return first?.value ?? '';
}

/** Interview plan / API user row (participants on the job interview plan). */
export type PlanInterviewerRow = {
  id: number;
  name?: string | null;
  avatar?: string | null;
  image_url?: string | null;
  [key: string]: unknown;
};

/** First non-empty portrait URL (`UserOption` uses `data.avatar`). */
function pickAvatarUrl(
  ...candidates: (string | null | undefined)[]
): string | undefined {
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return c;
  }
  return undefined;
}

function getInterviewerId(inv: unknown): number {
  if (inv !== null && typeof inv === 'object' && 'id' in inv) {
    const raw = (inv as { id: unknown }).id;
    if (raw !== undefined && raw !== null) return Number(raw);
  }
  return Number(inv);
}

/**
 * react-select + `UserOption` / `UserMultiValue` use `data.avatar` and `data.name` for the
 * Chakra Avatar — `label` alone is not enough.
 */
export function mapPlanInterviewerToAsyncSelectOption(
  p: PlanInterviewerRow
): PlanInterviewerRow & {
  value: number;
  label: string;
  name: string;
  avatar?: string | null;
} {
  const name = p.name ?? '';
  const avatar = pickAvatarUrl(p.avatar, p.image_url);
  return {
    ...p,
    value: p.id,
    label: name,
    name,
    ...(avatar !== undefined ? { avatar } : {}),
  };
}

export function mapParticipantIdToInterviewerOption(
  participantId: number,
  planInterviewers: PlanInterviewerRow[]
):
  | ReturnType<typeof mapPlanInterviewerToAsyncSelectOption>
  | {
      value: number;
      label: string;
      name: string;
    } {
  const p = planInterviewers.find((x) => x.id === participantId);
  if (p) return mapPlanInterviewerToAsyncSelectOption(p);
  const fallback = `ID ${participantId}`;
  return { value: participantId, label: fallback, name: fallback };
}

export function mapInterviewInterviewerToAsyncSelectOption(
  inv: unknown,
  planInterviewers: PlanInterviewerRow[]
):
  | ReturnType<typeof mapPlanInterviewerToAsyncSelectOption>
  | {
      value: number;
      label: string;
      name: string;
    } {
  const id = getInterviewerId(inv);
  const fromPlan = planInterviewers.find((p) => p.id === id);
  const row =
    inv !== null && typeof inv === 'object'
      ? (inv as Record<string, unknown>)
      : {};
  const name =
    (typeof row.name === 'string' ? row.name : '') ||
    fromPlan?.name ||
    `ID ${id}`;
  const avatar = pickAvatarUrl(
    typeof row.avatar === 'string' ? row.avatar : undefined,
    typeof row.image_url === 'string' ? row.image_url : undefined,
    fromPlan?.avatar,
    fromPlan?.image_url
  );

  return {
    ...(fromPlan ?? {}),
    ...row,
    value: id,
    label: name,
    name,
    ...(avatar !== undefined ? { avatar } : {}),
  };
}

type OfficeRow = { id: number; name?: string };

export function buildDisplayPlan(params: {
  existingSession:
    | { interview_config?: Record<string, unknown> }
    | null
    | undefined;
  existingInterview: Record<string, unknown> | null | undefined;
  interviewPlan: Record<string, unknown> | null | undefined;
  planInterviewers: { id: number; [key: string]: unknown }[];
  orgUsers?: { id: number; [key: string]: unknown }[];
  offices: OfficeRow[];
}): Record<string, unknown> {
  const {
    existingSession,
    existingInterview,
    interviewPlan,
    planInterviewers,
    orgUsers = [],
    offices,
  } = params;

  const resolveOfficeName = (atsOfficeId: number | null | undefined) => {
    if (atsOfficeId === null) return undefined;
    const office = offices?.find((o) => o.id === atsOfficeId);
    return office?.name;
  };

  if (existingSession?.interview_config) {
    const c = existingSession.interview_config;
    const participantIds = (c.participant_ids as number[]) || [];
    const loc = (c.location as string) ?? interviewPlan?.location;
    const atsOfficeId = normalizePlanOfficeId(c);
    /**
     * Merge planInterviewers + orgUsers so participants not in the freshly
     * fetched plan still resolve to a name/avatar. Plan entries win on
     * collision (richer fields like `recommended_score`).
     */
    const lookupById = new Map<number, { id: number; [k: string]: unknown }>();
    orgUsers.forEach((u) => {
      if (typeof u?.id === 'number') lookupById.set(u.id, u);
    });
    planInterviewers.forEach((p) => {
      if (typeof p?.id === 'number') lookupById.set(p.id, p);
    });
    const resolvedInterviewers = participantIds
      .map((id) => lookupById.get(id))
      .filter((p): p is { id: number; [k: string]: unknown } => Boolean(p));
    return {
      ...(interviewPlan || {}),
      name: (c.name as string) ?? interviewPlan?.name,
      duration: (c.duration_minutes as number) ?? interviewPlan?.duration,
      kind: (c.kind as string) ?? interviewPlan?.kind,
      interview_kind:
        (c.kind as string) ?? (interviewPlan?.interview_kind as string),
      location: loc,
      ...(loc === 'onsite' &&
        atsOfficeId !== null && {
          ats_office_id: atsOfficeId,
          office_name: resolveOfficeName(atsOfficeId),
        }),
      calendar_visibility:
        c.calendar_visibility === 'private'
          ? 'private'
          : ((interviewPlan?.calendar_visibility as string) ?? 'default'),
      note_taker_enabled:
        (c.note_taker_enabled as boolean) ?? interviewPlan?.note_taker_enabled,
      interviewers: resolvedInterviewers,
      participants: resolvedInterviewers,
    };
  }

  if (existingInterview && !existingSession) {
    const inv = existingInterview;
    const invOfficeNorm = normalizePlanOfficeId(inv);
    const duration =
      (inv.duration_minutes as number) ??
      (inv.start_time && inv.end_time
        ? Math.max(
            15,
            moment(inv.end_time as string).diff(
              moment(inv.start_time as string),
              'minutes'
            )
          )
        : undefined) ??
      interviewPlan?.duration;
    return {
      ...(interviewPlan || {}),
      duration,
      kind:
        (inv.kind as string) ??
        (inv.interview_kind as string) ??
        interviewPlan?.kind,
      interview_kind:
        (inv.interview_kind as string) ??
        (inv.kind as string) ??
        (interviewPlan?.interview_kind as string),
      location: (inv.location as string) ?? interviewPlan?.location,
      ...(((inv.location as string) ?? interviewPlan?.location) === 'onsite' &&
        invOfficeNorm !== null && {
          ats_office_id: invOfficeNorm,
          office_name: resolveOfficeName(invOfficeNorm),
        }),
      calendar_visibility:
        inv.calendar_visibility === 'private' ? 'private' : 'default',
      note_taker_enabled: (inv.note_taker_enabled as boolean) ?? false,
      interviewers: (inv.interviewers as unknown[]) ?? [],
      participants: (inv.interviewers as unknown[]) ?? [],
    };
  }

  const plan = interviewPlan || {};
  const planLoc = plan.location as string;
  const planOfficeIdFinite = normalizePlanOfficeId(plan);
  return {
    ...plan,
    ...(planLoc === 'onsite' &&
      planOfficeIdFinite !== null && {
        ats_office_id: planOfficeIdFinite,
        office_name: resolveOfficeName(planOfficeIdFinite),
      }),
  };
}

/** Single footer line when contact has a valid IANA `time_zone`. */
export function buildCandidateCurrentTimeLine(
  hasCandidateTimezone: boolean,
  candidateCurrentTime: string
): string | null {
  if (!hasCandidateTimezone) return null;
  return `${COPY.currentTimeForCandidate}: ${candidateCurrentTime}`;
}

/**
 * Maps a coordinator session UTC ISO time to custom timing state (15-min slot in picker TZ).
 */
export function parseCoordinatorScheduledAtToCustomSchedule(
  iso: string | null | undefined,
  timezoneKey: string
): { date: Date; time: string; timezoneKey: string } | null {
  if (!iso) return null;
  const m = moment.utc(iso).tz(timezoneKey);
  if (!m.isValid()) return null;
  const allowed = new Set(TIME_OPTIONS_15MIN.map((o) => o.value));
  let candidate = m.format('HH:mm');
  if (!allowed.has(candidate)) {
    const totalMinutes = m.hours() * 60 + m.minutes();
    const rounded = Math.round(totalMinutes / 15) * 15;
    const total = Math.min(Math.max(rounded, 0), 23 * 60 + 45);
    const h = Math.floor(total / 60);
    const min = total % 60;
    candidate = `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  if (!allowed.has(candidate)) {
    const target = m.hours() * 60 + m.minutes();
    let best = TIME_OPTIONS_15MIN[0]?.value ?? '09:00';
    let bestDiff = Infinity;
    for (const opt of TIME_OPTIONS_15MIN) {
      const [hh, mm] = opt.value.split(':').map(Number);
      const mins = hh * 60 + mm;
      const diff = Math.abs(mins - target);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = opt.value;
      }
    }
    candidate = best;
  }
  const ymd = m.format('YYYY-MM-DD');
  const dayStart = moment.tz(ymd, 'YYYY-MM-DD', timezoneKey).startOf('day');
  return {
    date: dayStart.toDate(),
    time: candidate,
    timezoneKey,
  };
}

/** @deprecated Prefer account-format display from useDateFormat in useScheduleWithAiForm. Kept for tests / callers. */
export function computeCustomScheduleDisplay(
  customSchedule: {
    date?: unknown;
    time?: string;
    timezoneKey?: string;
  } | null
): string | null {
  if (
    !customSchedule?.date ||
    !customSchedule?.time ||
    !customSchedule?.timezoneKey
  )
    return null;
  const m = moment.tz(
    `${moment(customSchedule.date).format('YYYY-MM-DD')} ${customSchedule.time}`,
    customSchedule.timezoneKey
  );
  return `${m.format('MMM D, YYYY [at] h:mm A')} ${formatTimezoneAbbreviationForDisplay(m, customSchedule.timezoneKey)}`;
}

/** Preset slots use `candidateTimezoneKey` (candidate when valid, else recruiter). */
export function computeScheduledAtISO(params: {
  timing: string;
  customSchedule: {
    date?: unknown;
    time?: string;
    timezoneKey?: string;
  } | null;
  candidateTimezoneKey?: string;
  /** Backward-compatible alias used by some callers. */
  userTimezoneKey?: string;
}): string | null {
  const { timing, customSchedule, candidateTimezoneKey, userTimezoneKey } =
    params;
  if (timing === 'immediately') return null;
  if (
    timing === 'custom' &&
    customSchedule?.date &&
    customSchedule?.time &&
    customSchedule?.timezoneKey
  ) {
    return moment
      .tz(
        `${moment(customSchedule.date).format('YYYY-MM-DD')} ${customSchedule.time}`,
        customSchedule.timezoneKey
      )
      .utc()
      .toISOString();
  }
  const requestedTz = candidateTimezoneKey || userTimezoneKey || '';
  const resolvedTz = moment.tz.zone(requestedTz)
    ? requestedTz
    : moment.tz.guess();
  const anchor = moment().tz(resolvedTz);
  if (!anchor) return null;
  let scheduledMoment;
  switch (timing) {
    case 'today_evening':
      scheduledMoment = anchor.clone().startOf('day').add(18, 'hours');
      break;
    case 'tomorrow_morning':
      scheduledMoment = anchor
        .clone()
        .add(1, 'day')
        .startOf('day')
        .add(9, 'hours');
      break;
    case 'tomorrow_afternoon':
      scheduledMoment = anchor
        .clone()
        .add(1, 'day')
        .startOf('day')
        .add(14, 'hours');
      break;
    case 'tomorrow_evening':
      scheduledMoment = anchor
        .clone()
        .add(1, 'day')
        .startOf('day')
        .add(18, 'hours');
      break;
    default:
      return null;
  }
  return scheduledMoment ? scheduledMoment.utc().toISOString() : null;
}
